// AGC: Firebase SDK initialization — single source of truth for app/auth/db/storage instances
// AGC: All files should import { auth, db, storage } from '../lib/firebase' (or '../../lib/firebase')
// AGC: instead of initializing Firebase inline.
// AGC: Offline persistence enabled — Firestore reads work offline via IndexedDB cache
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import { getFirestore, initializeFirestore, persistentLocalCache, persistentMultipleTabManager, type Firestore } from 'firebase/firestore'
import { getStorage, type FirebaseStorage } from 'firebase/storage'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID, // AGC: Analytics web fallback
}

// AGC: Singleton — prevent re-initialization during HMR
const app: FirebaseApp = getApps().length === 0
  ? initializeApp(firebaseConfig)
  : getApps()[0]!

export const auth: Auth = getAuth(app)

// AGC: Initialize Firestore with persistent local cache for offline support
// AGC: Uses IndexedDB under the hood — reads work offline, writes queue until online
// AGC: persistentMultipleTabManager allows multiple browser tabs (dev) without conflicts
// AGC: initializeFirestore throws if already called (e.g. HMR); fall back to getFirestore
function createFirestore(): Firestore {
  try {
    return initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    })
  } catch {
    return getFirestore(app)
  }
}

export const db: Firestore = createFirestore()

export const storage: FirebaseStorage = getStorage(app)
